import { playKnightSuriProcedural } from './audio'
import { EXEC_PUSH_DUST } from './exec_parry'
import type { FxEvent, Player } from './types'

// 大きく押し飛ばされた時の足の滑りと土煙 (2026-10-19・検収パック 1021 の E-1 ユーザー
//   「飛ばされて、足がズザーってなって、砂ぼこりが立つみたいな、そういうエフェクトとかがないから、弾いた瞬間スッと画面端に移動したり」)。
//   対象 = knockbackRemainingPx で押される物のうち大きい物 (介錯の咆哮の空気の波を弾いた 300px・組み変わりの押しのけ 264px)。
//   致命を弾いた押し戻し (execPush) は別の仕組みで、もう土煙がある (src/exec_parry.ts execPushDustFx)。絵はそれと同じ土煙を使う。
//   2026-10-19 検収パック 1022 で「直した版の方が良い」→ 配布版へ。前の版＝DEV ?knockSkid=now。描くだけ＝位置・当たりは1pxも変えない。
//   研究 = docs/research/2026-10-19-awoken-executioner-1021-fixes.md
export type KnockSkid = 'now' | '1021'
let mode: KnockSkid = '1021'
export function setKnockSkid(raw: string | null): void { mode = import.meta.env.DEV && raw === 'now' ? 'now' : '1021' }
export function knockSkid(): KnockSkid { return mode }

/** これより小さい押しには付けない (普通の被弾の小さな押しまで砂だらけにしない)。 */
export const KNOCK_SKID_MIN_PX = 120

/** 大きな押しが始まったコマか (音を鳴らす合図にも使う)。 */
export function knockSkidStarted(prev: Player, next: Player): boolean {
  if (mode !== '1021') return false
  const a = Math.abs(prev.knockbackRemainingPx ?? 0), b = Math.abs(next.knockbackRemainingPx ?? 0)
  return b >= KNOCK_SKID_MIN_PX && b > a + 60
}

export function knockSkidFx(prev: Player, next: Player, now: number): FxEvent[] {
  if (mode !== '1021') return []
  const rem = next.knockbackRemainingPx ?? 0
  const out: FxEvent[] = []
  if (knockSkidStarted(prev, next)) {
    const dir = Math.sign(rem)
    // 押された瞬間: 踏ん張った足元から、押される向きと逆へ土煙が弾ける。
    out.push({ kind: 'knight_lunge_dust', variant: 'kick', x: next.x, y: next.y, t: now, dir: -dir, amount: 0.9 })
    return out
  }
  const dx = next.x - prev.x
  if (rem === 0 || Math.abs(dx) < 0.5) return out
  const dir = Math.sign(dx)
  const D = EXEC_PUSH_DUST
  // 滑る速さ (1コマの移動量) で濃さを決める。最初の 84px/コマで最大、止まり際は薄く。
  const amount = D.minAmount + (D.maxAmount - D.minAmount) * Math.min(1, Math.abs(dx) / 40)
  // 後ろ足: 滑った所に床の擦り跡 (毎コマ・つながった線になる)。
  out.push({ kind: 'knight_lunge_dust', variant: 'scuff', x: prev.x + dir * D.backFootPx, x2: next.x + dir * D.backFootPx, y: next.y, t: now, dir: -dir, amount })
  // 前足: 床を押して土煙 (16px 進むごと)。
  if (Math.floor(Math.abs(next.x) / D.everyPx) !== Math.floor(Math.abs(prev.x) / D.everyPx)) {
    out.push({ kind: 'knight_lunge_dust', variant: 'kick', x: next.x - dir * D.frontFootPx, y: next.y, t: now, dir: -dir, amount: amount * 0.8 })
  }
  return out
}

/** 滑る音 (仮)。騎士のすり足の床の擦りを、滑る長さ 380ms で鳴らす。本番の音は音の専用セッションへ頼む。 */
export function knockSkidSound(prev: Player, next: Player): void {
  if (knockSkidStarted(prev, next)) playKnightSuriProcedural('glide', 380)
}

// 組み変わり (刃⇔腕砲の変形) の手応え (同じ E-1 ユーザー「変形するエフェクトとかが一切なくて、音もない」)。
//   今の版: 組み変わりの絵が出るだけで、始まり・押しのけの瞬間に床も空気も動かず、音もない。
//   1021 版: 組み変わりが始まる時に足元へ小さな土煙、押しのけが当たる瞬間に足元の両側へ土煙を弾けさせ、
//   仮の音 (重い振りの音＋踏み込みの音) を鳴らす。本番の音は音の専用セッションへ頼む。前の版＝DEV ?knockSkid=now。
type MorphHolder = { x: number; y: number; exec?: { morph?: string | null; morphElapsedMs: number; morphTurnRequired: boolean | null } | null }
export function morphShoveStarted(prev: MorphHolder, next: MorphHolder, shoveHitAtMs: (turn: boolean | null) => number): boolean {
  if (mode !== '1021') return false
  const a = prev.exec, b = next.exec
  if (!a || !b || a.morph !== 'morphing') return false
  const hit = shoveHitAtMs(a.morphTurnRequired)
  return a.morphElapsedMs < hit && (b.morph !== 'morphing' || b.morphElapsedMs >= hit)
}
export function morphFx(prev: MorphHolder, next: MorphHolder, now: number, shoveHitAtMs: (turn: boolean | null) => number): FxEvent[] {
  if (mode !== '1021') return []
  const a = prev.exec, b = next.exec
  const out: FxEvent[] = []
  if (b?.morph === 'morphing' && b.morphElapsedMs > 0 && (a?.morph !== 'morphing' || a.morphElapsedMs === 0)) {
    out.push({ kind: 'knight_lunge_dust', variant: 'kick', x: next.x, y: next.y, t: now, dir: 1, amount: 0.35 })
    out.push({ kind: 'knight_lunge_dust', variant: 'kick', x: next.x, y: next.y, t: now, dir: -1, amount: 0.35 })
  }
  if (morphShoveStarted(prev, next, shoveHitAtMs)) {
    out.push({ kind: 'knight_lunge_dust', variant: 'kick', x: next.x + 24, y: next.y, t: now, dir: 1, amount: 0.85 })
    out.push({ kind: 'knight_lunge_dust', variant: 'kick', x: next.x - 24, y: next.y, t: now, dir: -1, amount: 0.85 })
  }
  return out
}
